// ✅ src/screens/ChatScreen.js (FULL)
// ✅ Gifts system WORKING + consistent wallet fields
// ✅ FIXED: unread badge system
// ✅ NEW: creates notifications for receiver on TEXT/MEDIA/GIFT
// ✅ ✅ NEW: Leaderboard fields update (giftCoinsSent / giftCoinsReceived)
//
// ✅ UPDATE (ONLY):
// 1) ✅ Gift animation (pop + sparkle) when a gift message is newly added
// 2) ✅ Mobile fix: Send button always shows (composer grid becomes tighter on small screens)
// 3) ✅ NEW: Call logs render (type: "call") from CallScreen.js
// 4) ✅ FIX: call messages align correctly even if senderUid missing (uses fromUid fallback)
// 5) ✅ ✅ NEW: WhatsApp-style AUTO call log creation in chat by listening to /calls
// 6) ✅ ✅ FIX: chat opens at bottom + stays at bottom even when media loads

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  setDoc,
  limit,
  runTransaction,
  increment,
  where,
} from "firebase/firestore";
import { ref as sRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../lib/firebase";

// ✅ NEW
import { createNotification } from "../lib/notify";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function timeHHMM(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
function daysAgoFromMs(ms) {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const d = Math.max(0, Math.floor(diff / (24 * 3600 * 1000)));
  if (d <= 0) return "today";
  if (d === 1) return "1 day ago";
  return `${d} days ago`;
}
function parseMatchId(matchId) {
  const t = safeStr(matchId);
  const parts = t.split("__").filter(Boolean);
  if (parts.length !== 2) return { a: "", b: "" };
  return { a: parts[0], b: parts[1] };
}
function extFromName(name) {
  const n = safeStr(name);
  const i = n.lastIndexOf(".");
  if (i === -1) return "";
  return n.slice(i + 1).toLowerCase();
}
function guessTypeFromFile(file) {
  const mime = safeStr(file?.type);
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("image/")) return "image";

  const e = extFromName(file?.name);
  const img = ["jpg", "jpeg", "png", "gif", "webp", "heic"];
  const vid = ["mp4", "mov", "webm", "m4v", "3gp", "mkv"];
  if (vid.includes(e)) return "video";
  if (img.includes(e)) return "image";
  return "file";
}

/* ---------------- ✅ unread helpers ---------------- */
function myUnreadField(match, myUid) {
  return safeStr(match?.userA) === safeStr(myUid) ? "unreadCountA" : "unreadCountB";
}
function otherUnreadField(match, myUid) {
  return safeStr(match?.userA) === safeStr(myUid) ? "unreadCountB" : "unreadCountA";
}

/* ---------------- ✅ CALL helpers ---------------- */
function fmtMMSS(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
function callLine(m) {
  const mode = safeStr(m?.callMode).toLowerCase() === "video" ? "video" : "voice";
  const st = safeStr(m?.callStatus).toLowerCase();

  const icon = mode === "video" ? "🎥" : "📞";
  const label = mode === "video" ? "Video call" : "Voice call";

  const stText =
    st === "missed"
      ? "Missed call"
      : st === "declined"
      ? "Call declined"
      : st === "ended"
      ? "Call ended"
      : "Call";

  const dur = st === "ended" ? fmtMMSS(m?.durationSec) : "";

  return `${icon} ${label} • ${stText}${dur ? ` • ${dur}` : ""}`;
}

/* ---------------- wallet (auto-create) ---------------- */
async function ensureWallet(uid, email = "") {
  const u = safeStr(uid);
  if (!u) return doc(db, "wallets", "missing");

  const walletRef = doc(db, "wallets", u);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(walletRef);
    if (!snap.exists()) {
      tx.set(
        walletRef,
        {
          uid: u,
          email: safeStr(email),
          coins: 0,
          balance: 0,
          pending: 0,
          totalEarned: 0,
          totalWithdrawn: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  });

  return walletRef;
}

/* ---------------- Firebase Storage upload ---------------- */
async function uploadToFirebaseStorage({ file, matchId, meUid, onProgress }) {
  if (!storage) throw new Error("Firebase Storage not initialized. Export 'storage' from lib/firebase.js");
  if (!file) throw new Error("No file selected.");

  const type = guessTypeFromFile(file);
  if (type !== "image" && type !== "video") {
    throw new Error("Only images/videos are supported right now.");
  }

  const ext = extFromName(file.name) || (type === "image" ? "jpg" : "mp4");
  const safeName = `${Date.now()}_${Math.random().toString(16).slice(2)}.${ext}`;

  const path = `matches/${safeStr(matchId)}/media/${safeStr(meUid)}/${safeName}`;
  const fileRef = sRef(storage, path);

  const task = uploadBytesResumable(fileRef, file, {
    contentType: file.type || undefined,
  });

  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        if (typeof onProgress === "function") {
          const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
          onProgress(pct);
        }
      },
      (err) => reject(err),
      () => resolve()
    );
  });

  const url = await getDownloadURL(task.snapshot.ref);

  return {
    url,
    type,
    meta: {
      path,
      name: safeStr(file.name),
      size: Number(file.size || 0),
      mime: safeStr(file.type),
    },
  };
}

/* ---------------- responsive helper ---------------- */
function useIsSmall(maxWidth = 520) {
  const [isSmall, setIsSmall] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width:${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width:${maxWidth}px)`);
    const onChange = () => setIsSmall(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [maxWidth]);

  return isSmall;
}

/* ---------------- emoji picker ---------------- */
const EMOJIS = ["😀", "😁", "😂", "🤣", "😊", "😍", "😘", "🥰", "😎", "🤩", "😅", "😇", "😴", "😡", "😭", "🙏", "👍", "🔥", "💯", "✨"];

/* ---------------- gifts: auto-seed + live load ---------------- */
const SEED_GIFTS = [
  { id: "rose", name: "Rose", emoji: "🌹", coins: 10, sort: 1, isActive: true },
  { id: "tulip", name: "Tulip", emoji: "🌷", coins: 20, sort: 2, isActive: true },
  { id: "chocolate", name: "Chocolate", emoji: "🍫", coins: 25, sort: 3, isActive: true },
  { id: "icecream", name: "Ice Cream", emoji: "🍦", coins: 40, sort: 4, isActive: true },
  { id: "teddy", name: "Teddy Bear", emoji: "🧸", coins: 50, sort: 5, isActive: true },
  { id: "perfume", name: "Perfume", emoji: "🧴", coins: 75, sort: 6, isActive: true },
  { id: "ring", name: "Diamond Ring", emoji: "💍", coins: 100, sort: 7, isActive: true },
  { id: "bouquet", name: "Bouquet", emoji: "💐", coins: 150, sort: 8, isActive: true },
  { id: "heels", name: "Heels", emoji: "👠", coins: 200, sort: 9, isActive: true },
  { id: "crown", name: "Crown", emoji: "👑", coins: 250, sort: 10, isActive: true },
  { id: "giftbox", name: "Gift Box", emoji: "🎁", coins: 300, sort: 11, isActive: true },
  { id: "diamond", name: "Diamond", emoji: "💎", coins: 500, sort: 12, isActive: true },
  { id: "car", name: "Car", emoji: "🚗", coins: 750, sort: 13, isActive: true },
  { id: "rocket", name: "Rocket", emoji: "🚀", coins: 1000, sort: 14, isActive: true },
  { id: "yacht", name: "Yacht", emoji: "🛥️", coins: 1500, sort: 15, isActive: true },
  { id: "mansion", name: "Mansion", emoji: "🏠", coins: 2000, sort: 16, isActive: true },
  { id: "castle", name: "Castle", emoji: "🏰", coins: 2500, sort: 17, isActive: true },
  { id: "privatejet", name: "Private Jet", emoji: "🛩️", coins: 3500, sort: 18, isActive: true },
  { id: "galaxy", name: "Galaxy", emoji: "🌌", coins: 5000, sort: 19, isActive: true },
];

async function ensureGiftsSeed() {
  const giftsCol = collection(db, "gifts");
  const s = await getDocs(query(giftsCol, limit(1)));
  if (!s.empty) return;

  await Promise.all(
    SEED_GIFTS.map((g) =>
      setDoc(doc(db, "gifts", g.id), { ...g, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true })
    )
  );
}

/**
 * ✅ Optional: value of gifts in GHS for receiver earnings.
 * Example: 10 coins = ₵0.60 => 1 coin = ₵0.06
 */
const GIFT_EARN_RATE_GHS = 0.06;

export default function ChatScreen() {
  const nav = useNavigate();
  const { matchId } = useParams();
  const isSmall = useIsSmall(520);

  const [me, setMe] = useState(null);
  const [matchMeta, setMatchMeta] = useState({ createdAtMs: null });
  const [other, setOther] = useState({
    uid: "",
    displayName: "Chat",
    avatar: "",
    online: true,
    lastActiveText: "Just now",
  });

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  // popup states
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [giftsOpen, setGiftsOpen] = useState(false);

  // message menu
  const [msgMenuOpen, setMsgMenuOpen] = useState(false);
  const [activeMsg, setActiveMsg] = useState(null);

  // ✅ WhatsApp-like "pending media"
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingType, setPendingType] = useState("");
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const [gifts, setGifts] = useState([]);

  // ✅ wallet coins
  const [coinBalance, setCoinBalance] = useState(0);
  const [walletReady, setWalletReady] = useState(false);

  // ✅ gift sending loading
  const [sendingGiftId, setSendingGiftId] = useState("");

  const listRef = useRef(null);
  const endRef = useRef(null); // ✅ NEW
  const fileRef = useRef(null);

  // ✅ NEW: gift animation tracking (only for newly added gift messages)
  const prevMsgIdsRef = useRef(new Set());
  const [animGiftIds, setAnimGiftIds] = useState(() => new Set());
  const animTimersRef = useRef({});

  // ✅ NEW: avoid double processing call logs locally
  const seenCallIdsRef = useRef(new Set());

  // ✅ NEW: stable scroll-to-bottom that survives media loading
  const scrollToBottom = (behavior = "auto") => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        endRef.current?.scrollIntoView({ behavior, block: "end" });
      });
    });
  };

  const closeAllOverlays = () => {
    setEmojiOpen(false);
    setGiftsOpen(false);
    setMsgMenuOpen(false);
    setActiveMsg(null);
  };

  const clearPendingMedia = () => {
    setPendingFile(null);
    setPendingType("");
    setUploadPct(0);
    setPendingPreviewUrl((prev) => {
      if (prev) {
        try {
          URL.revokeObjectURL(prev);
        } catch {}
      }
      return "";
    });
  };

  // ✅ helper to notify receiver
  const notifyOther = useCallback(
    async ({ type, title, message, route }) => {
      if (!me?.uid || !other?.uid || !matchId) return;

      const myLabel = safeStr(auth.currentUser?.displayName) || safeStr(me?.email) || "Someone";
      const msg = safeStr(message);

      try {
        await createNotification(other.uid, {
          type: safeStr(type) || "MESSAGE",
          title: safeStr(title) || "New update",
          message: msg || `${myLabel} sent you an update`,
          data: {
            route: safeStr(route) || `/chat/${matchId}`,
            matchId: safeStr(matchId),
            fromUid: safeStr(me.uid),
          },
        });
      } catch (e) {
        console.error("notifyOther error:", e);
      }
    },
    [me?.uid, me?.email, other?.uid, matchId]
  );

  // auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setMe({ uid: u.uid, email: safeStr(u.email) });
    });
    return () => unsub();
  }, [nav]);

  // ✅ wallet listener (auto-create) — reads coins field
  useEffect(() => {
    if (!me?.uid) return;

    let unsub = null;

    (async () => {
      try {
        setWalletReady(false);
        await ensureWallet(me.uid, me.email);

        unsub = onSnapshot(
          doc(db, "wallets", me.uid),
          (snap) => {
            const d = snap.exists() ? snap.data() || {} : {};
            setCoinBalance(num(d.coins));
            setWalletReady(true);
          },
          () => setWalletReady(true)
        );
      } catch {
        setWalletReady(true);
      }
    })();

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [me?.uid, me?.email]);

  // close overlays on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAllOverlays();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // cleanup object URL
  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) {
        try {
          URL.revokeObjectURL(pendingPreviewUrl);
        } catch {}
      }
    };
  }, [pendingPreviewUrl]);

  // ✅ cleanup gift animation timers
  useEffect(() => {
    const timers = animTimersRef.current; // ✅ copy once for cleanup
    return () => {
      try {
        const t = timers || {};
        Object.keys(t).forEach((k) => clearTimeout(t[k]));
      } catch {}
    };
  }, []);

  // gifts load
  useEffect(() => {
    let unsub = null;

    (async () => {
      try {
        await ensureGiftsSeed();
        const giftsQ = query(collection(db, "gifts"), orderBy("sort", "asc"));
        unsub = onSnapshot(
          giftsQ,
          (snap) => {
            const arr = [];
            snap.forEach((d) => {
              const g = d.data() || {};
              if (g.isActive === false) return;
              arr.push({
                id: d.id,
                name: safeStr(g.name),
                emoji: safeStr(g.emoji) || "🎁",
                coins: Number(g.coins || 0),
                sort: Number(g.sort || 999),
              });
            });
            setGifts(arr);
          },
          (e) => console.error("gifts listen error:", e)
        );
      } catch (e) {
        console.error("ensureGiftsSeed error:", e);
      }
    })();

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  // load match + other + messages
  useEffect(() => {
    if (!me?.uid || !matchId) return;

    let unSubMsgs = null;

    (async () => {
      setLoading(true);
      const matchRef = doc(db, "matches", matchId);

      try {
        let matchSnap = await getDoc(matchRef);

        if (!matchSnap.exists()) {
          const { a, b } = parseMatchId(matchId);

          if (!a || !b) {
            setOther({ uid: "", displayName: "Chat not found", avatar: "", online: false, lastActiveText: "" });
            setMessages([]);
            setLoading(false);
            return;
          }

          if (me.uid !== a && me.uid !== b) {
            setOther({ uid: "", displayName: "Not allowed", avatar: "", online: false, lastActiveText: "" });
            setMessages([]);
            setLoading(false);
            return;
          }

          await setDoc(
            matchRef,
            {
              createdAt: serverTimestamp(),
              isActive: true,
              lastMessageAt: serverTimestamp(),
              lastMessageText: "",
              unreadCountA: 0,
              unreadCountB: 0,
              userA: a,
              userB: b,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          matchSnap = await getDoc(matchRef);
        }

        const m = matchSnap.data();
        const otherUid = safeStr(m.userA) === me.uid ? safeStr(m.userB) : safeStr(m.userA);

        const createdAtMs = msFromTs(m.createdAt) || null;
        setMatchMeta({ createdAtMs });

        try {
          const profSnap = await getDoc(doc(db, "datingProfiles", otherUid));
          if (profSnap.exists()) {
            const p = profSnap.data();
            const name = safeStr(p.displayName) || "Chat";
            const avatar = safeStr(p.avatar) || safeStr(p.photos?.[0]);
            setOther({ uid: otherUid, displayName: name, avatar, online: true, lastActiveText: "Just now" });
          } else {
            setOther({ uid: otherUid, displayName: "Chat", avatar: "", online: true, lastActiveText: "Just now" });
          }
        } catch {
          setOther({ uid: otherUid, displayName: "Chat", avatar: "", online: true, lastActiveText: "Just now" });
        }

        const msgsQ = query(collection(db, "matches", matchId, "messages"), orderBy("createdAt", "asc"));

        unSubMsgs = onSnapshot(
          msgsQ,
          (snap) => {
            const list = [];
            snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

            // ✅ detect newly added gift messages -> animate once
            try {
              const prev = prevMsgIdsRef.current || new Set();
              const next = new Set(list.map((x) => safeStr(x.id)));

              const added = [];
              next.forEach((id) => {
                if (!prev.has(id)) added.push(id);
              });

              if (added.length) {
                const addedGiftIds = [];
                for (const id of added) {
                  const msg = list.find((x) => safeStr(x.id) === id);
                  if (safeStr(msg?.type) === "gift") addedGiftIds.push(id);
                }

                if (addedGiftIds.length) {
                  setAnimGiftIds((old) => {
                    const s = new Set(old);
                    addedGiftIds.forEach((id) => s.add(id));
                    return s;
                  });

                  addedGiftIds.forEach((id) => {
                    if (animTimersRef.current[id]) clearTimeout(animTimersRef.current[id]);
                    animTimersRef.current[id] = setTimeout(() => {
                      setAnimGiftIds((old) => {
                        const s = new Set(old);
                        s.delete(id);
                        return s;
                      });
                      try {
                        delete animTimersRef.current[id];
                      } catch {}
                    }, 1200);
                  });
                }
              }

              prevMsgIdsRef.current = next;
            } catch {}

            setMessages(list);

            // ✅ NEW: scroll to bottom (stable)
            scrollToBottom("auto");
          },
          (err) => console.error("messages listen error:", err)
        );

        // ✅ reset MY unread to 0 when opening chat
        try {
          const myField = myUnreadField(m, me.uid);
          await updateDoc(matchRef, { [myField]: 0 });
        } catch {}
      } catch (e) {
        console.error("Chat load error:", e);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (unSubMsgs) unSubMsgs();
    };
  }, [me?.uid, matchId, nav]);

  // ✅ NEW: when chat opens/finishes loading -> bottom
  useEffect(() => {
    if (loading) return;
    scrollToBottom("auto");
  }, [loading, matchId]);

  // ✅ NEW: when number of messages changes -> bottom
  useEffect(() => {
    if (loading) return;
    scrollToBottom("auto");
  }, [messages.length, loading]);

  const title = useMemo(() => safeStr(other.displayName) || "Chat", [other.displayName]);

  const matchedBannerText = useMemo(() => {
    const d = daysAgoFromMs(matchMeta.createdAtMs);
    return `You matched with ${safeStr(other.displayName) || "them"} ${d}.`;
  }, [matchMeta.createdAtMs, other.displayName]);

  /* ---------------- bump unread + last preview ---------------- */
  const bumpUnreadAndLast = async (previewText) => {
    if (!me?.uid || !matchId) return;

    const matchRef = doc(db, "matches", matchId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(matchRef);
      if (!snap.exists()) return;
      const m = snap.data() || {};
      const otherField = otherUnreadField(m, me.uid);

      tx.update(matchRef, {
        lastMessageText: safeStr(previewText),
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        [otherField]: increment(1),
      });
    });
  };

  /* ---------------- ✅✅ WhatsApp-style AUTO CALL LOGS ---------------- */
  useEffect(() => {
    if (!me?.uid || !matchId) return;
    if (!other?.uid) return;

    const qy = query(
      collection(db, "calls"),
      where("matchId", "==", safeStr(matchId)),
      where("status", "in", ["ended", "missed", "declined"]),
      orderBy("createdAt", "desc"),
      limit(25)
    );

    const unsub = onSnapshot(
      qy,
      async (snap) => {
        if (snap.empty) return;

        for (const d of snap.docs) {
          const callId = d.id;
          if (!callId) continue;

          if (seenCallIdsRef.current.has(callId)) continue;

          const c = d.data() || {};
          const fromUid = safeStr(c.fromUid);
          const toUid = safeStr(c.toUid);
          const status = safeStr(c.status).toLowerCase();
          const mode = safeStr(c.mode).toLowerCase() === "video" ? "video" : "voice";
          const durationSec = num(c.durationSec);

          const isThisPair =
            (fromUid === safeStr(me.uid) && toUid === safeStr(other.uid)) ||
            (fromUid === safeStr(other.uid) && toUid === safeStr(me.uid));

          if (!isThisPair) continue;

          if (c.loggedToChat === true) {
            seenCallIdsRef.current.add(callId);
            continue;
          }

          try {
            await runTransaction(db, async (tx) => {
              const callRef = doc(db, "calls", callId);
              const callSnap = await tx.get(callRef);
              if (!callSnap.exists()) return;

              const cd = callSnap.data() || {};
              if (cd.loggedToChat === true) return;

              const matchRef = doc(db, "matches", matchId);
              const matchSnap = await tx.get(matchRef);
              if (!matchSnap.exists()) return;

              const match = matchSnap.data() || {};
              const otherField = otherUnreadField(match, me.uid);

              const msgRef = doc(collection(db, "matches", matchId, "messages"));

              const prettyMode = mode === "video" ? "Video call" : "Voice call";
              const prettyStatus =
                status === "missed" ? "Missed call" : status === "declined" ? "Call declined" : "Call ended";
              const prettyDur = status === "ended" && durationSec > 0 ? ` • ${fmtMMSS(durationSec)}` : "";
              const preview = `${mode === "video" ? "🎥" : "📞"} ${prettyMode} • ${prettyStatus}${prettyDur}`;

              tx.set(msgRef, {
                type: "call",
                callId,
                callMode: mode,
                callStatus: status,
                durationSec: durationSec || 0,
                fromUid,
                toUid,
                senderUid: fromUid,
                createdAt: serverTimestamp(),
              });

              tx.set(
                matchRef,
                {
                  lastMessageText: preview,
                  lastMessageAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  [otherField]: increment(1),
                },
                { merge: true }
              );

              tx.set(
                callRef,
                {
                  loggedToChat: true,
                  loggedAt: serverTimestamp(),
                },
                { merge: true }
              );
            });

            seenCallIdsRef.current.add(callId);

            if (status === "missed" || status === "declined") {
              const txt = mode === "video" ? "🎥 Video call" : "📞 Voice call";
              await notifyOther({
                type: "CALL",
                title: "Call update",
                message: `${txt} • ${status === "missed" ? "Missed call" : "Call declined"}`,
                route: `/chat/${matchId}`,
              });
            }
          } catch (e) {
            console.error("auto call log error:", e);
          }
        }
      },
      (e) => console.error("calls listen error:", e)
    );

    return () => unsub();
  }, [me?.uid, other?.uid, matchId, notifyOther]);

  // send media or text
  const send = async () => {
    if (!me?.uid || !matchId) return;
    if (uploading) return;

    const caption = safeStr(text);
    if (!pendingFile && !caption) return;

    // media
    if (pendingFile) {
      try {
        closeAllOverlays();
        setUploading(true);
        setUploadPct(0);

        const fileToSend = pendingFile;
        const captionToSend = caption;

        setText("");
        clearPendingMedia();

        const uploaded = await uploadToFirebaseStorage({
          file: fileToSend,
          matchId,
          meUid: me.uid,
          onProgress: (pct) => setUploadPct(pct),
        });

        await addDoc(collection(db, "matches", matchId, "messages"), {
          type: uploaded.type,
          mediaUrl: uploaded.url,
          mediaMeta: uploaded.meta,
          text: captionToSend,
          senderUid: me.uid,
          createdAt: serverTimestamp(),
        });

        const preview =
          uploaded.type === "video"
            ? captionToSend
              ? `📹 ${captionToSend}`
              : "📹 Video"
            : captionToSend
            ? `🖼️ ${captionToSend}`
            : "🖼️ Photo";

        await bumpUnreadAndLast(preview);

        await notifyOther({
          type: "MESSAGE",
          title: "New message",
          message: preview,
          route: `/chat/${matchId}`,
        });
      } catch (e) {
        console.error("send media error:", e);
        alert(e?.message || "Failed to send media.");
      } finally {
        setUploading(false);
        setUploadPct(0);
      }
      return;
    }

    // text
    if (caption) {
      setText("");
      try {
        await addDoc(collection(db, "matches", matchId, "messages"), {
          type: "text",
          text: caption,
          senderUid: me.uid,
          createdAt: serverTimestamp(),
        });

        await bumpUnreadAndLast(caption);

        await notifyOther({
          type: "MESSAGE",
          title: "New message",
          message: caption,
          route: `/chat/${matchId}`,
        });
      } catch (e) {
        console.error("send error:", e);
      }
    }
  };

  // ✅ Gift send (transaction safe) + ✅ Leaderboard update (TOTALS ACCUMULATE)
  const sendGift = async (g) => {
    if (!me?.uid || !matchId) return;
    if (!other?.uid) return;

    const gift = {
      id: safeStr(g?.id),
      name: safeStr(g?.name) || "Gift",
      emoji: safeStr(g?.emoji) || "🎁",
      coins: num(g?.coins),
    };
    if (!gift.id || gift.coins <= 0) return;

    closeAllOverlays();
    setSendingGiftId(gift.id);

    try {
      const matchRef = doc(db, "matches", matchId);
      const msgRef = doc(collection(db, "matches", matchId, "messages"));

      const senderWalletRef = doc(db, "wallets", me.uid);
      const receiverWalletRef = doc(db, "wallets", other.uid);

      const ledgerRef = doc(collection(db, "giftSends"));
      const notifRef = doc(collection(db, "notifications", other.uid, "items"));

      const senderProfileRef = doc(db, "datingProfiles", me.uid);
      const receiverProfileRef = doc(db, "datingProfiles", other.uid);

      const earnGhs = +(gift.coins * GIFT_EARN_RATE_GHS).toFixed(2);

      await runTransaction(db, async (tx) => {
        const matchSnap = await tx.get(matchRef);
        if (!matchSnap.exists()) return;
        const match = matchSnap.data() || {};
        const otherField = otherUnreadField(match, me.uid);

        // sender wallet
        const senderSnap = await tx.get(senderWalletRef);
        const sender = senderSnap.exists() ? senderSnap.data() || {} : {};
        const senderCoins = num(sender.coins);

        if (!senderSnap.exists()) {
          tx.set(
            senderWalletRef,
            {
              uid: me.uid,
              email: safeStr(me.email),
              coins: 0,
              balance: 0,
              pending: 0,
              totalEarned: 0,
              totalWithdrawn: 0,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        if (senderCoins < gift.coins) {
          throw new Error("Not enough coins. Please buy coins to send gifts.");
        }

        // receiver wallet
        const receiverSnap = await tx.get(receiverWalletRef);
        if (!receiverSnap.exists()) {
          tx.set(
            receiverWalletRef,
            {
              uid: other.uid,
              coins: 0,
              balance: 0,
              pending: 0,
              totalEarned: 0,
              totalWithdrawn: 0,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        // debit coins
        tx.update(senderWalletRef, {
          coins: senderCoins - gift.coins,
          updatedAt: serverTimestamp(),
        });

        // credit earnings
        if (earnGhs > 0) {
          tx.set(receiverWalletRef, { updatedAt: serverTimestamp() }, { merge: true });
          tx.update(receiverWalletRef, {
            balance: increment(earnGhs),
            totalEarned: increment(earnGhs),
            updatedAt: serverTimestamp(),
          });
        }

        // message
        tx.set(msgRef, {
          type: "gift",
          giftId: gift.id,
          giftName: gift.name,
          giftEmoji: gift.emoji,
          giftCoins: gift.coins,
          giftEarnGhs: earnGhs,
          senderUid: me.uid,
          toUid: other.uid,
          createdAt: serverTimestamp(),
        });

        // match preview + unread
        tx.set(
          matchRef,
          {
            lastMessageText: `${gift.emoji} ${gift.name}`,
            lastMessageAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            [otherField]: increment(1),
          },
          { merge: true }
        );

        // ledger
        tx.set(ledgerRef, {
          fromUid: me.uid,
          toUid: other.uid,
          matchId: safeStr(matchId),
          giftId: gift.id,
          giftName: gift.name,
          giftEmoji: gift.emoji,
          coins: gift.coins,
          earnGhs,
          createdAt: serverTimestamp(),
        });

        // ✅✅ LEADERBOARD TOTALS (NO RESET)
        tx.set(
          senderProfileRef,
          {
            giftCoinsSent: increment(gift.coins),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        tx.set(
          receiverProfileRef,
          {
            giftCoinsReceived: increment(gift.coins),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // wallet transaction logs
        const senderTxRef = doc(collection(db, "wallets", me.uid, "transactions"));
        tx.set(senderTxRef, {
          type: "GIFT_SENT",
          direction: "DEBIT",
          coins: gift.coins,
          giftId: gift.id,
          toUid: other.uid,
          refId: msgRef.id,
          createdAt: serverTimestamp(),
        });

        const receiverTxRef = doc(collection(db, "wallets", other.uid, "transactions"));
        tx.set(receiverTxRef, {
          type: "GIFT_RECEIVED",
          direction: "CREDIT",
          coins: gift.coins,
          earnGhs,
          giftId: gift.id,
          fromUid: me.uid,
          refId: msgRef.id,
          createdAt: serverTimestamp(),
        });

        const myLabel = safeStr(auth.currentUser?.displayName) || safeStr(me.email) || "Someone";

        tx.set(notifRef, {
          type: "MESSAGE",
          title: "New gift 🎁",
          message: `${myLabel} sent you ${gift.emoji} ${gift.name}`,
          data: {
            route: `/chat/${matchId}`,
            matchId: safeStr(matchId),
            fromUid: safeStr(me.uid),
          },
          read: false,
          createdAt: serverTimestamp(),
        });
      });

      scrollToBottom("auto");
    } catch (e) {
      console.error("sendGift error:", e);
      const msg = safeStr(e?.message) || "Failed to send gift.";

      if (msg.toLowerCase().includes("not enough")) {
        const go = window.confirm(`${msg}\n\nGo to Buy Gifts now?`);
        if (go) nav("/buy-gifts");
      } else {
        alert(msg);
      }
    } finally {
      setSendingGiftId("");
    }
  };

  // pick file -> pending only
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !me?.uid || !matchId) return;

    setTimeout(() => {
      if (fileRef.current) fileRef.current.value = "";
    }, 0);

    try {
      closeAllOverlays();

      const type = guessTypeFromFile(f);
      if (type !== "image" && type !== "video") {
        alert("Only images/videos are supported right now.");
        return;
      }

      if (pendingPreviewUrl) {
        try {
          URL.revokeObjectURL(pendingPreviewUrl);
        } catch {}
      }

      const previewUrl = URL.createObjectURL(f);
      setPendingFile(f);
      setPendingType(type);
      setPendingPreviewUrl(previewUrl);
    } catch (err) {
      console.error("pick file error:", err);
      alert(err?.message || "Failed to pick file.");
    }
  };

  const openMsgMenu = (m) => {
    setActiveMsg({
      id: m.id,
      senderUid: safeStr(m.senderUid || m.fromUid),
      text: safeStr(m.text),
      type: safeStr(m.type),
      mediaUrl: safeStr(m.mediaUrl),
    });
    setMsgMenuOpen(true);
    setEmojiOpen(false);
    setGiftsOpen(false);
  };

  const deleteMessage = async () => {
    const msgId = safeStr(activeMsg?.id);
    if (!msgId || !matchId) return;

    if (safeStr(activeMsg?.senderUid) !== safeStr(me?.uid)) {
      alert("You can only delete your own message.");
      closeAllOverlays();
      return;
    }

    try {
      await deleteDoc(doc(db, "matches", matchId, "messages", msgId));
    } catch (e) {
      console.error("delete message error:", e);
    } finally {
      closeAllOverlays();
    }
  };

  const reportMessage = () => {
    const msgId = safeStr(activeMsg?.id);
    closeAllOverlays();

    nav(`/report/${matchId}`, {
      state: {
        matchId,
        reportedUid: other.uid,
        messageId: msgId,
        messageText: safeStr(activeMsg?.text),
        chatName: safeStr(other.displayName),
        mediaUrl: safeStr(activeMsg?.mediaUrl),
        type: safeStr(activeMsg?.type),
      },
    });
  };

  return (
    <div style={S.page}>
      {/* ✅ keyframes (only for gift animation) */}
      <style>{`
        @keyframes giftPop {
          0%   { transform: scale(0.88); opacity: 0.2; filter: blur(0.2px); }
          55%  { transform: scale(1.06); opacity: 1; filter: blur(0px); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes sparkleFloat {
          0%   { transform: translateY(6px) scale(0.9); opacity: 0; }
          35%  { opacity: 1; }
          100% { transform: translateY(-10px) scale(1.1); opacity: 0; }
        }
      `}</style>

      <div style={S.shell}>
        {/* Header (sticky) */}
        <div style={S.header}>
          <button type="button" onClick={() => nav("/chats")} style={S.backBtn} title="Back">
            ←
          </button>

          <button
            type="button"
            onClick={() => {
              if (!other.uid) return;
              nav("/profile-details", { state: { uid: other.uid } });
            }}
            style={S.headInfo}
            title="Open profile"
          >
            <div style={{ position: "relative" }}>
              {other.avatar ? (
                <img src={other.avatar} alt={title} style={S.headAvatar} />
              ) : (
                <div style={S.headAvatarFallback}>{initials(title)}</div>
              )}
              {other.online ? <span style={S.onlineDot} /> : null}
            </div>

            <div style={{ minWidth: 0, textAlign: "left" }}>
              <div style={S.hTitle} title={title}>
                {title}
              </div>
              <div style={S.hSub}>
                {other.online ? "Online" : "Offline"} • Last Active: {safeStr(other.lastActiveText) || "—"}
              </div>
            </div>
          </button>

          <div style={S.hActions}>
            <div style={S.balancePill} title="Your coins">
              🪙 {walletReady ? coinBalance : "…"}
            </div>

            <button
              type="button"
              style={S.hActionBtn}
              title="Voice call"
              onClick={() => nav(`/call/${matchId}`, { state: { channelId: matchId, mode: "voice" } })}
            >
              📞
            </button>

            <button
              type="button"
              style={S.hActionBtn}
              title="Video call"
              onClick={() => nav(`/call/${matchId}`, { state: { channelId: matchId, mode: "video" } })}
            >
              🎥
            </button>
          </div>
        </div>

        {/* Chat card */}
        <div style={S.chatCard}>
          <div style={S.banner}>{matchedBannerText}</div>

          {/* ONLY THIS SCROLLS */}
          <div style={S.list} ref={listRef}>
            {loading ? (
              <div style={S.loading}>Loading…</div>
            ) : messages.length ? (
              <>
                {messages.map((m) => {
                  const mine = safeStr(m.senderUid || m.fromUid) === safeStr(me?.uid);
                  const ms = msFromTs(m.createdAt);
                  const type = safeStr(m.type) || "text";
                  const mediaUrl = safeStr(m.mediaUrl);
                  const caption = safeStr(m.text);

                  const giftEmoji = safeStr(m.giftEmoji);
                  const giftName = safeStr(m.giftName);
                  const giftCoins = num(m.giftCoins);

                  const shouldAnimGift = type === "gift" && animGiftIds.has(safeStr(m.id));

                  return (
                    <div key={m.id} style={{ ...S.bubbleRow, justifyContent: mine ? "flex-end" : "flex-start" }}>
                      <div style={{ ...S.msgRowWrap, flexDirection: mine ? "row-reverse" : "row" }}>
                        <button type="button" title="Message options" style={S.msgDots} onClick={() => openMsgMenu(m)}>
                          ⋯
                        </button>

                        <div style={{ ...S.bubble, ...(mine ? S.bubbleMine : S.bubbleOther) }}>
                          {type === "image" && mediaUrl ? (
                            <>
                              <img
                                src={mediaUrl}
                                alt="sent"
                                style={S.mediaImg}
                                onLoad={() => scrollToBottom("auto")}
                              />
                              {caption ? <div style={{ ...S.bText, marginTop: 8 }}>{caption}</div> : null}
                            </>
                          ) : type === "video" && mediaUrl ? (
                            <>
                              <video
                                src={mediaUrl}
                                controls
                                style={S.mediaVid}
                                onLoadedMetadata={() => scrollToBottom("auto")}
                              />
                              {caption ? <div style={{ ...S.bText, marginTop: 8 }}>{caption}</div> : null}
                            </>
                          ) : type === "gift" ? (
                            <div style={{ ...S.giftBubble, ...(shouldAnimGift ? S.giftAnimWrap : null) }}>
                              {shouldAnimGift ? (
                                <>
                                  <span style={{ ...S.sparkle, left: 10, top: -8, animationDelay: "0ms" }}>✨</span>
                                  <span style={{ ...S.sparkle, right: 8, top: -10, animationDelay: "90ms" }}>💫</span>
                                  <span style={{ ...S.sparkle, left: 24, bottom: -10, animationDelay: "160ms" }}>✨</span>
                                </>
                              ) : null}

                              <div style={{ ...S.giftEmoji, ...(shouldAnimGift ? S.giftAnimEmoji : null) }}>
                                {giftEmoji || "🎁"}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={S.giftTitle}>
                                  {mine ? "You sent" : "Sent"} {giftName || "a gift"}
                                </div>
                                <div style={S.giftSub}>{giftCoins} coins</div>
                              </div>
                            </div>
                          ) : type === "call" ? (
                            <div style={S.callBubble}>{callLine(m)}</div>
                          ) : (
                            <div style={S.bText}>{caption}</div>
                          )}

                          <div style={S.bTime}>{timeHHMM(ms)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div ref={endRef} />
              </>
            ) : (
              <>
                <div style={S.empty}>Say hi 👋</div>
                <div ref={endRef} />
              </>
            )}
          </div>

          {/* Composer */}
          <div style={S.composerWrap}>
            {pendingFile ? (
              <div style={S.pendingBar}>
                <div style={S.pendingLeft}>
                  {pendingType === "image" ? (
                    <img src={pendingPreviewUrl} alt="preview" style={S.pendingThumb} />
                  ) : (
                    <video src={pendingPreviewUrl} style={S.pendingThumb} />
                  )}

                  <div style={{ minWidth: 0 }}>
                    <div style={S.pendingName} title={safeStr(pendingFile?.name)}>
                      {safeStr(pendingFile?.name)}
                    </div>
                    <div style={S.pendingSub}>
                      {pendingType === "video" ? "Video ready" : "Photo ready"}
                      {uploading ? ` • Uploading ${uploadPct}%` : ""}
                    </div>
                  </div>
                </div>

                <button type="button" style={S.pendingX} onClick={clearPendingMedia} disabled={uploading} title="Remove">
                  ✕
                </button>
              </div>
            ) : null}

            <div style={{ ...S.composer, ...(isSmall ? S.composerSmall : null) }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={onPickFile}
                multiple={false}
              />

              <button
                type="button"
                style={S.iconBtn}
                title="Attach file"
                onClick={() => {
                  closeAllOverlays();
                  if (fileRef.current) fileRef.current.click();
                }}
                disabled={uploading}
              >
                +
              </button>

              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={pendingFile ? "Add a caption..." : "Type a message..."}
                style={S.input}
                onKeyDown={(e) => {
                  if (e.key === "Enter") send();
                }}
                disabled={uploading}
              />

              <button
                type="button"
                style={S.pillBtn}
                title="Gifts"
                onClick={() => {
                  setGiftsOpen((v) => !v);
                  setEmojiOpen(false);
                  setMsgMenuOpen(false);
                  setActiveMsg(null);
                }}
                disabled={uploading}
              >
                🎁
              </button>

              <button
                type="button"
                style={S.pillBtn}
                title="Emoji"
                onClick={() => {
                  setEmojiOpen((v) => !v);
                  setGiftsOpen(false);
                  setMsgMenuOpen(false);
                  setActiveMsg(null);
                }}
                disabled={uploading}
              >
                😀
              </button>

              <button
                type="button"
                onClick={send}
                style={{ ...S.sendBtn, ...(isSmall ? S.sendBtnSmall : null) }}
                title="Send"
                disabled={uploading}
              >
                {uploading ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>

        {/* Overlays */}
        {giftsOpen || emojiOpen || msgMenuOpen ? (
          <div style={S.overlay} onClick={closeAllOverlays}>
            {msgMenuOpen ? (
              <div style={S.actionSheet} onClick={(e) => e.stopPropagation()}>
                <div style={S.actionTitle}>Message</div>

                <button type="button" style={S.actionBtn} onClick={reportMessage}>
                  🚩 Report
                </button>

                <button type="button" style={S.actionBtnDanger} onClick={deleteMessage}>
                  🗑️ Delete
                </button>

                <button type="button" style={S.actionBtnGhost} onClick={closeAllOverlays}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                {isSmall ? (
                  <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
                    <div style={S.sheetTop}>
                      <div style={S.sheetTitle}>
                        {giftsOpen ? "Gifts" : "Emojis"}{" "}
                        {giftsOpen ? <span style={S.sheetCoins}>• 🪙 {walletReady ? coinBalance : "…"}</span> : null}
                      </div>
                      <button type="button" style={S.sheetClose} onClick={closeAllOverlays}>
                        ✕
                      </button>
                    </div>

                    <div style={S.sheetBody}>
                      {giftsOpen ? (
                        <div style={S.giftGridSheet}>
                          {gifts.map((g) => {
                            const busy = sendingGiftId === g.id;
                            return (
                              <button
                                key={g.id}
                                type="button"
                                style={{ ...S.giftBtn, ...(busy ? S.giftBtnOff : null) }}
                                onClick={() => sendGift(g)}
                                disabled={busy}
                              >
                                <div style={{ fontSize: 22 }}>{g.emoji}</div>
                                <div style={S.giftName}>{g.name}</div>
                                <div style={S.giftCoins}>{g.coins} coins</div>
                                <div style={S.giftBuyHint}>{busy ? "Sending..." : "Tap to send"}</div>
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            style={S.buyMoreBtn}
                            onClick={() => {
                              closeAllOverlays();
                              nav("/buy-gifts");
                            }}
                          >
                            ➕ Buy more coins
                          </button>
                        </div>
                      ) : (
                        <div style={S.emojiGridSheet}>
                          {EMOJIS.map((em) => (
                            <button
                              key={em}
                              type="button"
                              style={S.emojiBtn}
                              onClick={() => setText((t) => (t ? `${t}${em}` : em))}
                            >
                              {em}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={S.dropdown} onClick={(e) => e.stopPropagation()}>
                    <div style={S.pickerTitleRow}>
                      <div style={S.pickerTitleText}>
                        {giftsOpen ? "Gifts" : "Emojis"}{" "}
                        {giftsOpen ? <span style={S.sheetCoins}>• 🪙 {walletReady ? coinBalance : "…"}</span> : null}
                      </div>
                      <button type="button" style={S.pickerX} onClick={closeAllOverlays}>
                        ✕
                      </button>
                    </div>

                    {giftsOpen ? (
                      <div style={S.giftGrid}>
                        {gifts.map((g) => {
                          const busy = sendingGiftId === g.id;
                          return (
                            <button
                              key={g.id}
                              type="button"
                              style={{ ...S.giftBtn, ...(busy ? S.giftBtnOff : null) }}
                              onClick={() => sendGift(g)}
                              disabled={busy}
                            >
                              <div style={{ fontSize: 20 }}>{g.emoji}</div>
                              <div style={S.giftName}>{g.name}</div>
                              <div style={S.giftCoins}>{g.coins} coins</div>
                              <div style={S.giftBuyHint}>{busy ? "Sending..." : "Tap to send"}</div>
                            </button>
                          );
                        })}

                        <button
                          type="button"
                          style={S.buyMoreBtn}
                          onClick={() => {
                            closeAllOverlays();
                            nav("/buy-gifts");
                          }}
                        >
                          ➕ Buy more coins
                        </button>
                      </div>
                    ) : (
                      <div style={S.emojiGrid}>
                        {EMOJIS.map((em) => (
                          <button
                            key={em}
                            type="button"
                            style={S.emojiBtn}
                            onClick={() => setText((t) => (t ? `${t}${em}` : em))}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ---------------- styles (FULL) ---------------- */
/* ---------------- styles (FULL) ---------------- */
const BOTTOM_NAV_HEIGHT = 70; // ✅ adjust if your bottom nav is taller (try 80)

const S = {
  page: {
    width: "100%",
    boxSizing: "border-box",
    position: "relative",
    height: "100dvh",
    overflow: "hidden",
  },

  shell: {
    width: "100%",
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  header: {
    display: "grid",
    gridTemplateColumns: "44px 1fr auto",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    marginBottom: 10,
    position: "sticky",
    top: 0,
    zIndex: 60,
    background: "rgba(236, 242, 238, 0.85)",
    backdropFilter: "blur(10px)",
  },

  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "none",
    background: "rgba(255,255,255,0.6)",
    cursor: "pointer",
    fontSize: 20,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 12px 22px rgba(17,24,39,0.06)",
    fontWeight: 950,
  },

  headInfo: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    padding: 0,
  },

  headAvatar: {
    width: 44,
    height: 44,
    borderRadius: 999,
    objectFit: "cover",
    boxShadow: "0 14px 26px rgba(17,24,39,0.12)",
  },

  headAvatarFallback: {
    width: 44,
    height: 44,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(59,130,246,0.25), rgba(99,102,241,0.20))",
    color: "#111827",
    fontWeight: 900,
    border: "1px solid rgba(17,24,39,0.10)",
  },

  onlineDot: {
    position: "absolute",
    right: 2,
    bottom: 2,
    width: 12,
    height: 12,
    borderRadius: 999,
    background: "#22c55e",
    border: "2px solid #fff",
  },

  hTitle: {
    fontWeight: 950,
    fontSize: 18,
    letterSpacing: -0.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  hSub: {
    fontWeight: 800,
    opacity: 0.65,
    fontSize: 12,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  hActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },

  balancePill: {
    height: 44,
    padding: "0 12px",
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(255,255,255,0.92)",
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    boxShadow: "0 10px 18px rgba(17,24,39,0.06)",
  },

  hActionBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 18,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 10px 18px rgba(17,24,39,0.06)",
  },

  chatCard: {
    width: "100%",
    borderRadius: 22,
    border: "1px solid rgba(17,24,39,0.06)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 18px 40px rgba(17,24,39,0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    backdropFilter: "blur(10px)",
  },

  banner: {
    margin: 12,
    minHeight: 40,
    borderRadius: 999,
    background: "linear-gradient(90deg, rgba(99,102,241,0.22), rgba(59,130,246,0.18))",
    color: "#111827",
    fontWeight: 900,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 14px 22px rgba(17,24,39,0.06)",
    padding: "8px 12px",
    textAlign: "center",
    border: "1px solid rgba(17,24,39,0.06)",
  },

  // ✅ ONLY THIS SCROLLS
  // ✅ paddingBottom so last messages don't hide under composer + bottom nav
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: 14,
    paddingTop: 6,
    paddingBottom: BOTTOM_NAV_HEIGHT + 160, // ✅ important
    WebkitOverflowScrolling: "touch",
  },

  loading: {
    display: "grid",
    placeItems: "center",
    height: 240,
    fontWeight: 900,
  },

  empty: {
    padding: 14,
    borderRadius: 18,
    border: "1px dashed rgba(17,24,39,0.16)",
    background: "rgba(255,255,255,0.65)",
    fontWeight: 800,
    opacity: 0.85,
  },

  bubbleRow: { display: "flex", marginBottom: 10 },

  msgRowWrap: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    maxWidth: "92%",
  },

  msgDots: {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 20,
    lineHeight: "20px",
    boxShadow: "0 10px 18px rgba(17,24,39,0.06)",
    display: "grid",
    placeItems: "center",
  },

  bubble: {
    maxWidth: "78%",
    padding: "10px 12px",
    borderRadius: 18,
    border: "1px solid rgba(17,24,39,0.06)",
    boxShadow: "0 10px 22px rgba(17,24,39,0.06)",
  },

  bubbleMine: {
    background: "linear-gradient(180deg, rgba(59,130,246,0.98), rgba(37,99,235,0.98))",
    color: "#fff",
    borderTopRightRadius: 10,
  },

  bubbleOther: {
    background: "rgba(255,255,255,0.96)",
    color: "#111827",
    borderTopLeftRadius: 10,
  },

  bText: {
    fontWeight: 850,
    lineHeight: 1.35,
    fontSize: 14,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  giftBubble: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 180,
    position: "relative",
  },

  giftEmoji: { fontSize: 28 },

  giftTitle: { fontWeight: 1000, fontSize: 14, lineHeight: 1.2 },

  giftSub: { fontWeight: 900, opacity: 0.7, fontSize: 12, marginTop: 2 },

  giftAnimWrap: { animation: "giftPop 520ms ease-out both" },

  giftAnimEmoji: { transformOrigin: "50% 60%", animation: "giftPop 520ms ease-out both" },

  sparkle: {
    position: "absolute",
    fontSize: 14,
    pointerEvents: "none",
    animation: "sparkleFloat 720ms ease-out both",
    filter: "drop-shadow(0 10px 12px rgba(17,24,39,0.10))",
  },

  callBubble: {
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(17,24,39,0.06)",
    border: "1px solid rgba(17,24,39,0.08)",
    fontWeight: 950,
    fontSize: 13,
    opacity: 0.9,
    maxWidth: "78%",
  },

  // ✅ media sizing (prevents tall media from blowing up the bubble)
  mediaImg: {
    width: "100%",
    maxWidth: 260,
    maxHeight: 360,
    borderRadius: 14,
    display: "block",
    border: "1px solid rgba(17,24,39,0.10)",
    objectFit: "cover",
  },

  mediaVid: {
    width: "100%",
    maxWidth: 280,
    maxHeight: 360, // ✅ big fix
    borderRadius: 14,
    display: "block",
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(0,0,0,0.85)",
    objectFit: "cover",
  },

  bTime: {
    marginTop: 6,
    fontSize: 11,
    opacity: 0.75,
    fontWeight: 900,
    textAlign: "right",
  },

  // ✅ FIX: composer must sit ABOVE bottom nav
  // Using fixed is stronger than sticky (prevents it being hidden)
  composerWrap: {
    borderTop: "1px solid rgba(17,24,39,0.08)",
    background: "rgba(255,255,255,0.9)",
    position: "fixed",
    left: 0,
    right: 0,
    bottom: BOTTOM_NAV_HEIGHT, // ✅ lift above bottom nav
    zIndex: 999,
    paddingBottom: "env(safe-area-inset-bottom)",
  },

  pendingBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderBottom: "1px solid rgba(17,24,39,0.08)",
    background: "rgba(255,255,255,0.96)",
  },

  pendingLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },

  pendingThumb: {
    width: 46,
    height: 46,
    borderRadius: 12,
    objectFit: "cover",
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(0,0,0,0.06)",
  },

  pendingName: {
    fontWeight: 950,
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 260,
  },

  pendingSub: {
    fontWeight: 850,
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  pendingX: {
    width: 38,
    height: 38,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 1000,
  },

  composer: {
    padding: 12,
    display: "grid",
    gridTemplateColumns: "44px 1fr 44px 44px 90px",
    gap: 10,
    alignItems: "center",
  },

  composerSmall: {
    gridTemplateColumns: "40px 1fr 40px 40px 76px",
    gap: 8,
    padding: 10,
  },

  iconBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 22,
  },

  input: {
    height: 44,
    borderRadius: 999,
    border: "1px solid rgba(17,24,39,0.10)",
    padding: "0 14px",
    outline: "none",
    fontWeight: 800,
    background: "#fff",
  },

  pillBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 18,
  },

  sendBtn: {
    height: 44,
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 900,
    color: "#fff",
    background: "linear-gradient(90deg, rgba(59,130,246,0.98), rgba(99,102,241,0.98))",
    boxShadow: "0 16px 24px rgba(59,130,246,0.18)",
  },

  sendBtnSmall: { fontSize: 14, padding: 0 },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.28)",
    backdropFilter: "blur(6px)",
    zIndex: 9999,
    display: "grid",
    placeItems: "end center",
    padding: 14,
    boxSizing: "border-box",
  },

  dropdown: {
    placeSelf: "end end",
    width: 320,
    maxWidth: "calc(100vw - 28px)",
    maxHeight: "calc(100vh - 120px)",
    overflow: "auto",
    borderRadius: 16,
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(17,24,39,0.10)",
    boxShadow: "0 22px 60px rgba(17,24,39,0.22)",
    padding: 10,
  },

  pickerTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "4px 2px 10px 2px",
    borderBottom: "1px solid rgba(17,24,39,0.06)",
    marginBottom: 10,
  },

  pickerTitleText: { fontWeight: 1000, opacity: 0.85 },

  pickerX: {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 1000,
  },

  sheet: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 18,
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(17,24,39,0.10)",
    boxShadow: "0 22px 60px rgba(17,24,39,0.22)",
    overflow: "hidden",
  },

  sheetTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderBottom: "1px solid rgba(17,24,39,0.08)",
  },

  sheetTitle: { fontWeight: 1000 },

  sheetCoins: { fontWeight: 1000, opacity: 0.7, marginLeft: 6 },

  sheetClose: {
    width: 38,
    height: 38,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 1000,
  },

  sheetBody: { maxHeight: "56vh", overflow: "auto", padding: 12 },

  emojiGrid: { display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6 },

  emojiGridSheet: { display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 8 },

  emojiBtn: {
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(17,24,39,0.08)",
    background: "#fff",
    cursor: "pointer",
    fontSize: 18,
  },

  giftGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 },

  giftGridSheet: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 },

  giftBtn: {
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.08)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    display: "grid",
    placeItems: "center",
    gap: 4,
    textAlign: "center",
  },

  giftBtnOff: { opacity: 0.7, cursor: "not-allowed" },

  giftName: { fontSize: 12, fontWeight: 1000, opacity: 0.85 },

  giftCoins: { fontSize: 12, fontWeight: 900, opacity: 0.6 },

  giftBuyHint: { fontSize: 11, fontWeight: 900, opacity: 0.55, marginTop: 4 },

  buyMoreBtn: {
    gridColumn: "1 / -1",
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(99,102,241,0.10)",
    padding: 12,
    cursor: "pointer",
    fontWeight: 1000,
  },

  actionSheet: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 18,
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(17,24,39,0.10)",
    boxShadow: "0 28px 80px rgba(17,24,39,0.28)",
    overflow: "hidden",
    padding: 12,
    placeSelf: "end center",
  },

  actionTitle: { fontWeight: 1000, opacity: 0.75, marginBottom: 10, padding: "4px 6px" },

  actionBtn: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    textAlign: "left",
    marginBottom: 10,
  },

  actionBtnDanger: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.25)",
    background: "rgba(239,68,68,0.08)",
    cursor: "pointer",
    fontWeight: 1000,
    textAlign: "left",
    marginBottom: 10,
    color: "#991b1b",
  },

  actionBtnGhost: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "none",
    background: "rgba(17,24,39,0.06)",
    cursor: "pointer",
    fontWeight: 950,
    textAlign: "center",
  },
};